import React, { useState, useEffect } from 'react';
import './Pages.css';

const FLEET_API_BASE = 'https://main.victriedemos.com:3017';
const FLEET_INTERMEDIARY_OID = 76040;

const PostedDeclarations = () => {
  const [fleets, setFleets] = useState([]);
  const [loadingFleets, setLoadingFleets] = useState(true);
  const [fleetsError, setFleetsError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [entryCounts, setEntryCounts] = useState({});

  const [viewingFleet, setViewingFleet] = useState(null);
  const [fleetEntries, setFleetEntries] = useState([]);
  const [loadingEntries, setLoadingEntries] = useState(false);
  const [entriesError, setEntriesError] = useState('');
  const [entrySearch, setEntrySearch] = useState('');

  useEffect(() => {
    fetchApprovedFleets();
  }, []);

  const fetchApprovedFleets = async () => {
    setLoadingFleets(true);
    setFleetsError('');
    try {
      const res = await fetch(`${FLEET_API_BASE}/api/staff/getApprovedIntermediaryFleet?agentCode=${FLEET_INTERMEDIARY_OID}`);
      const data = await res.json();
      if (data.success) {
        const approved = data.data || [];
        setFleets(approved);
        fetchEntryCounts(approved);
      } else {
        setFleetsError(data.message || 'Failed to load posted declarations');
      }
    } catch (err) {
      console.error('Error fetching approved fleets:', err);
      setFleetsError('Unable to connect to fleet server');
    } finally {
      setLoadingFleets(false);
    }
  };

  const fetchEntryCounts = (fleetList) => {
    fleetList.forEach((f) => {
      fetch(`${FLEET_API_BASE}/api/staff/getFleetEntries?fleetNo=${encodeURIComponent(f.FleetNo)}`)
        .then((res) => res.json())
        .then((data) => {
          setEntryCounts(prev => ({ ...prev, [f.FleetNo]: data.success ? (data.data || []).length : null }));
        })
        .catch((err) => {
          console.error('Error fetching entry count for', f.FleetNo, err);
          setEntryCounts(prev => ({ ...prev, [f.FleetNo]: null }));
        });
    });
  };

  const openFleetEntries = async (fleet) => {
    setViewingFleet(fleet);
    setFleetEntries([]);
    setEntriesError('');
    setEntrySearch('');
    setLoadingEntries(true);
    try {
      const res = await fetch(`${FLEET_API_BASE}/api/staff/getFleetEntries?fleetNo=${encodeURIComponent(fleet.FleetNo)}`);
      const data = await res.json();
      if (data.success) {
        setFleetEntries(data.data || []);
      } else {
        setEntriesError(data.message || 'Failed to load fleet entries');
      }
    } catch (err) {
      console.error('Error fetching fleet entries:', err);
      setEntriesError('Unable to connect to fleet server');
    } finally {
      setLoadingEntries(false);
    }
  };

  const closeFleetEntries = () => {
    setViewingFleet(null);
    setFleetEntries([]);
  };

  const formatCurrency = (amount) => new Intl.NumberFormat('en-KE', { style: 'currency', currency: 'KES' }).format(amount || 0);
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const filteredFleets = fleets.filter(f => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (f.FleetNo || '').toString().toLowerCase().includes(term);
  });

  const filteredEntries = fleetEntries.filter(e => {
    if (!entrySearch) return true;
    const term = entrySearch.toLowerCase();
    return (e.Narration || '').toLowerCase().includes(term) || (e.CustomerName || '').toLowerCase().includes(term);
  });

  if (viewingFleet) {
    return (
      <div className="page-container">
        <div className="page-header">
          <div>
            <h1>📄 Fleet Entries — {viewingFleet.FleetNo}</h1>
            <p className="page-subtitle">{viewingFleet.ProductDescription}</p>
          </div>
          <button className="back-to-policies-btn" onClick={closeFleetEntries}>← Back to Posted Declarations</button>
        </div>

        <div className="search-bar">
          <input
            type="text"
            placeholder="🔍 Search by registration or customer name..."
            value={entrySearch}
            onChange={(e) => setEntrySearch(e.target.value)}
            className="search-input"
          />
        </div>

        {entriesError && <div className="error-banner">{entriesError}</div>}

        {loadingEntries ? (
          <div className="loading-container">
            <div className="spinner"></div>
            <p>Loading entries...</p>
          </div>
        ) : filteredEntries.length === 0 ? (
          <div className="empty-state">
            <p>No entries found</p>
          </div>
        ) : (
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Registration</th>
                  <th>Customer Name</th>
                  <th>Product</th>
                  <th>Net Premium</th>
                  <th>Commencement</th>
                  <th>Expiry</th>
                </tr>
              </thead>
              <tbody>
                {filteredEntries.map((e, i) => (
                  <tr key={e.OID}>
                    <td>{i + 1}</td>
                    <td>{e.Narration}</td>
                    <td>{e.CustomerName}</td>
                    <td>{e.ProductDescription}</td>
                    <td className="amount">{formatCurrency(e.NetPremium)}</td>
                    <td>{formatDate(e.CommencementDate)}</td>
                    <td>{formatDate(e.ExpiryDate)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1>📋 Posted Declarations</h1>
          <p className="page-subtitle">Approved fleet declarations</p>
        </div>
        <div className="badge-count">{filteredFleets.length} Fleets</div>
      </div>

      <div className="search-bar">
        <input
          type="text"
          placeholder="🔍 Search by Fleet No..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
      </div>

      {fleetsError && <div className="error-banner">{fleetsError}</div>}

      {loadingFleets ? (
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading posted declarations...</p>
        </div>
      ) : filteredFleets.length === 0 ? (
        <div className="empty-state">
          <p>No approved fleet declarations yet</p>
        </div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Fleet No</th>
                <th>Product</th>
                <th>Entries</th>
                <th>Premium</th>
                <th>Date Done</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredFleets.map(f => (
                <tr key={f.OID}>
                  <td className="policy-number">{f.FleetNo}</td>
                  <td>{f.ProductDescription || f.Product}</td>
                  <td style={{ textAlign: 'center' }}>{entryCounts[f.FleetNo] != null ? entryCounts[f.FleetNo] : '…'}</td>
                  <td className="amount">{formatCurrency(f.FleetPremium)}</td>
                  <td>{formatDate(f.DateDone)}</td>
                  <td>
                    <span className="status-badge approved">Approved</span>
                  </td>
                  <td>
                    <div className="row-actions">
                      <button type="button" className="btn btn-small btn-view" onClick={() => openFleetEntries(f)}>👁️ View</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default PostedDeclarations;
